import React, { useCallback, useState } from 'react';
import { isGlobalVersionTwo, isSASCGroupAccount } from '../helpers/Helpers';
import { prepareMarketingConsent } from '../helpers/MarketingConsent';
import { Input } from 'antd';
import { UNIVERSITY_IDS } from '../common/accountIds';

// Alternative component for specific university ID
const Alternative = (props) => {
    if (props.universityId !== UNIVERSITY_IDS.UST_SYDNEY) {
        return null;
    }
    return (
        <div className="checkbox-text">
            Alternatively you can email UTS at <a href="mailto:international@uts.edu.au">international@uts.edu.au</a> if
            you choose not to consent.
        </div>
    );
};
// Marketing Consent
const MarketingConsent = (props) => {
    return (
        <div className="checkbox-text">
            <label className="check-label">
                <Input
                    type="checkbox"
                    className="checkbox"
                    name="marketing"
                    value={props.isMarketingChecked}
                    checked={props.isMarketingChecked}
                    onChange={props.handleMarketingChange}
                    aria-label="Allow contact me"
                    aria-required="true"
                />
                <span className="checkbox-icon" />
                <p className="checkbox-text">{props.marketingConsent}</p>
            </label>
        </div>
    );
};

// Marketing Consent for THE group
const GlobalVersionTwoTerms = (props) => {
    return (
        <p className="checkbox-text accept-terms-text the-terms-text">
            By signing up you agree that you are over the age of 13. THE Student Chat is powered by The Ambassador
            Platform Ltd (TAP). By clicking Sign up I agree to&nbsp;
            <a
                href="https://legal.theambassadorplatform.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-link-color"
            >
                TAP&apos;s Terms
            </a>
            . When you use THE Student Chat, TAP will process your personal data according to its&nbsp;
            <a
                href="https://legal.theambassadorplatform.com/privacy-notice?hsLang=en"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-link-color"
            >
                Privacy Notice
            </a>
            , and {props.universityName} will process your personal data in accordance with its&nbsp;
            <a href={props.privacyLink} target="_blank" rel="noopener noreferrer" className="terms-link-color">
                Privacy Policy
            </a>
            . THE does not process this personal data.
        </p>
    );
};

// Marketing Consent text for SASCG group
const SASCGroupTermsText = (props) => {
    return (
        <p className="checkbox-text">
            By signing up you confirm that you are over the age of 18 and that you understand that the Study Australia
            Student Chat is a service provided by The Ambassador Platform Ltd. (TAP) together with participating
            Australian education providers. By clicking "sign up" you agree to TAP’s&nbsp;
            <a
                href="https://legal.theambassadorplatform.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-link-color"
            >
                Terms of Service
            </a>
            . You further understand that when you use the service, TAP will process your personal data according to
            its&nbsp;
            <a
                href="https://legal.theambassadorplatform.com/privacy-notice?hsLang=en"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-link-color"
            >
                Privacy Notice
            </a>
            , and {props.universityName} will process your personal data in accordance with its&nbsp;
            <a href={props.privacyLink} target="_blank" rel="noopener noreferrer" className="terms-link-color">
                Privacy Policy
            </a>
            . You also understand the Australian Government (Study Australia) is neither collecting nor processing the
            personal information you provide.
        </p>
    );
};

//Default  Marketing Consent text
const DefaultTermsText = (props) => {
    return (
        <p className="checkbox-text">
            I confirm that I am over the age of {props.minimumAge} and I have read, understood and agree with the&nbsp;
            <a
                href="https://legal.theambassadorplatform.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-link-color"
            >
                Terms of Service
            </a>
            . I further understand that my personal data may be processed by the&nbsp;
            {props.universityName} in accordance with its&nbsp;
            <a href={props.privacyLink} target="_blank" rel="noopener noreferrer" className="terms-link-color">
                Privacy Policy
            </a>
            &nbsp; and The Ambassador Platform will store and process my personal data within the United Kingdom in
            accordance with its&nbsp;
            <a href="https://tap.st/privacy" target="_blank" rel="noopener noreferrer" className="terms-link-color">
                Privacy Notice
            </a>
        </p>
    );
};

// Default Marketing Consent
const Terms = (props) => {
    const isSASCGroup = isSASCGroupAccount();

    return (
        <div className="checkbox-text">
            <label className="sign-up-form-label" style={{ marginBottom: '1rem' }}>
                <div className="check-label">
                    <Input
                        type="checkbox"
                        className="checkbox"
                        name="privacy"
                        value={props.isPrivacyChecked}
                        checked={props.isPrivacyChecked}
                        onChange={props.handlePrivacyChange}
                        aria-label="Please agree to the terms"
                        aria-required="true"
                    />
                    <span className="checkbox-icon" />
                    {isSASCGroup ? <SASCGroupTermsText {...props} /> : <DefaultTermsText {...props} />}
                </div>
                {(props.privacyChecked || props.isPrivacyChanged) && !props.isPrivacyChecked && (
                    <span className="error" id="error-privacy" style={{ marginBottom: '1rem' }}>
                        Please agree to the terms first
                    </span>
                )}
            </label>
        </div>
    );
};

const AcceptTermsText = (props) => {
    const [privacyChecked, setPrivacyChecked] = useState(false);

    let privacyLink = props.privacyLink;
    if (privacyLink && !privacyLink.startsWith('http')) {
        privacyLink = 'http://' + privacyLink;
    }

    // Callback for handling privacy change
    const handlePrivacyChange = useCallback((elem) => {
        setPrivacyChecked(true);
        props.handlePrivacyChange(elem);
    }, []);

    // Prepare marketing consent based on universityName
    const marketingConsent = prepareMarketingConsent(props.universityName);

    if (isGlobalVersionTwo) {
        return <GlobalVersionTwoTerms {...props} privacyLink={privacyLink} />;
    } else {
        return (
            <>
                <Terms {...props} handlePrivacyChange={handlePrivacyChange} privacyChecked={privacyChecked} />
                {marketingConsent && <MarketingConsent {...props} marketingConsent={marketingConsent} />}

                <Alternative universityId={props.universityId} />
            </>
        );
    }
};

const AcceptTerms = (props) => {
    return <AcceptTermsText {...props} />;
};

export default AcceptTerms;
